import { useMemo } from 'react';
import { useAppSelector } from './app/hooks';
import {
  selectCustomThemePalette,
  selectTheme,
} from './features/view/viewSlice';
import { selectIsAuthenticated } from './features/auth/authSlice';
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';
import ToggleColorPreference from '@components/Layout/Header/ToggleColorPreference';
import SessionExpiredDialog from '@components/Modals/SessionExpiredDialog';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ErrorSnackbar from '@components/Utility/CustomAlerts/ErrorSnackbar';
import InfoSnackbar from '@components/Utility/CustomAlerts/InfoSnackbar';
import SuccessSnackbar from '@components/Utility/CustomAlerts/SuccessSnackbar';
import WarningSnackbar from '@components/Utility/CustomAlerts/WarningSnackbar';
import ScheduledMaintenanceBanner from '@components/Maintenance/ScheduledMaintenanceBanner';
import { setDetectDelay } from 'devtools-detector';
import AppRouter from './AppRouter';

declare module 'notistack' {
  interface VariantOverrides {
    success: {
      title?: string;
      moreInfo?: string;
    };
    error: {
      title?: string;
      moreInfo?: string;
    };
    info: {
      title?: string;
      moreInfo?: string;
    };
    warning: {
      title?: string;
      moreInfo?: string;
    };
  }
}

setDetectDelay(1000);

const App = () => {
  const colorMode = useAppSelector(selectTheme);
  const themePallete = useAppSelector(selectCustomThemePalette);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const theme = useMemo(
    () =>
      responsiveFontSizes(
        createTheme({
          cssVariables: true,
          palette: {
            mode: colorMode,
            contrastThreshold: 3,
            primary: {
              main: themePallete.primary.main, // '#556cd6',
            },
            secondary: {
              main: themePallete.secondary.main, //'#19857b',
            },
            error: {
              main: themePallete.error.main,
            },
            success: {
              main: themePallete.success.main, // #40bf6d <- This is a great looking green text color but not so good on the sidebar
            },
            warning: {
              main: themePallete.warning.main,
            },
            info: {
              main: themePallete.info.main,
            },
            background: {
              default:
                colorMode == 'light'
                  ? themePallete.background.default.light
                  : themePallete.background.default.dark, // #fafafa : #303030
              paper:
                colorMode == 'light'
                  ? themePallete.background.paper.light
                  : themePallete.background.paper.dark, // "#fff" : "#424242"
            },
          },
          components: {
            MuiUseMediaQuery: {
              defaultProps: {
                noSsr: true,
              },
            },
            MuiDialog: {
              styleOverrides: {
                paper: ({ theme }) =>
                  theme.unstable_sx({
                    margin: { xs: 1, sm: 2, md: 3 },
                  }),
              },
            },
            MuiButton: {
              defaultProps: {
                variant: 'contained',
              },
            },
            // MuiDialogContent: {
            //   styleOverrides: {
            //     root: ({ theme }) =>
            //       theme.unstable_sx({
            //         paddingX: { xs: 1, sm: 2, md: 2.5 },
            //         paddingY: { xs: 1, sm: 2 },
            //       }),
            //   },
            // },
          },
        }),
        {},
      ),
    [colorMode, themePallete],
  );

  return (
    <ThemeProvider theme={theme} noSsr>
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-US">
        <SnackbarProvider
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          Components={{
            success: SuccessSnackbar,
            error: ErrorSnackbar,
            warning: WarningSnackbar,
            info: InfoSnackbar,
          }}
          style={{ maxWidth: 400 }}
        >
          <CssBaseline />

          {/* @ts-expect-error this does exist */}
          <pwa-install
            use-local-storage="true"
            manifest-url="/manifest.webmanifest"
          >
            {/* @ts-expect-error this does exist */}
          </pwa-install>

          {!isAuthenticated && (
            <ToggleColorPreference
              sx={{
                position: 'absolute',
                right: '1%',
                top: '6px',
                bgcolor: 'background.default',
                color: 'text.primary',
                zIndex: 2,
              }}
            />
          )}

          <AppRouter />

          {/* We need these dialogs placed here to show it while the login component is showing */}
          <SessionExpiredDialog />
          <ScheduledMaintenanceBanner />
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
